import type { ReactNode } from 'react';

import { getLocale } from '../../../i18n';
import { Absent, EstimateLabel, Withheld } from '../../../chrome/measure';
import { count } from '../../../chrome/measure/format';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import type { Territory, TerritoryCell } from '../api/schemas';
import { labellingOf } from './estimate';
import { tt } from './messages';

type Cells = NonNullable<Territory['cells']>;

const SEGMENT_TONES = ['bg-scale-1', 'bg-scale-2', 'bg-scale-3', 'bg-scale-4', 'bg-scale-5'];

/** Cells cycle through the five-step scale in the order they arrive. */
function toneOf(index: number): string {
  return SEGMENT_TONES[index % SEGMENT_TONES.length] ?? 'bg-scale-3';
}

/** A cell is named by the attributes it was returned with, age first: the tool
 *  returns bundles of attributes, and joining a bundle's cells into one age
 *  band would be a sum this screen may not compute. */
function labelOf(cell: TerritoryCell): string | null {
  const values = Object.entries(cell.attributes)
    .sort(([a], [b]) => Number(b === 'age_band') - Number(a === 'age_band'))
    .flatMap(([, value]) => (value === null ? [] : [value]));
  return values.length === 0 ? null : values.join(' · ');
}

/** How many cells a card lists before the rest collapse: a register crossed by
 *  age, gender and schooling returns hundreds of bundles. */
const SHOWN_CELLS = 8;

function sameLabelling(
  a: ReturnType<typeof labellingOf>,
  b: ReturnType<typeof labellingOf>,
): boolean {
  return (
    a.kind === b.kind &&
    (a.kind !== 'estimate' ||
      (b.kind === 'estimate' &&
        a.methodVersion === b.methodVersion &&
        a.confidence === b.confidence))
  );
}

function CellRow(props: {
  cell: TerritoryCell;
  tone: string;
  overall: ReturnType<typeof labellingOf>;
}): ReactNode {
  const { cell } = props;
  const labelling = labellingOf(cell);
  const label = labelOf(cell);
  return (
    <li className="text-caption text-muted-foreground flex items-center gap-2">
      <span aria-hidden className={`size-2 shrink-0 rounded-pill ${props.tone}`} />
      <span>{label ?? <Absent reason="not_measured" />}</span>
      {labelling.kind === 'refused' ? (
        <Absent reason="not_measured" />
      ) : (
        <span data-numeric>{count(cell.count, getLocale())}</span>
      )}
      {labelling.kind === 'estimate' && !sameLabelling(labelling, props.overall) && (
        <EstimateLabel methodVersion={labelling.methodVersion} confidence={labelling.confidence} />
      )}
    </li>
  );
}

/** The voters on the electoral register, from `get_territory`. The base is the
 *  register, a census of voters, so the commenter-sample statement that
 *  belongs to a post's composition is never shown here. The withheld share is
 *  stated before the first cell. */
export function CompositionCard(props: { territory: Territory }): ReactNode {
  const { territory } = props;
  const cells: Cells | null = territory.cells ?? null;
  const suppressedShare = territory.suppressed_share;
  const overall = labellingOf(territory);
  // Largest first, each keeping its tone from the bar above.
  const ranked = (cells ?? [])
    .map((cell, index) => ({ cell, index }))
    .sort((a, b) => b.cell.count - a.cell.count);

  return (
    <Card>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-title-sm">{tt('composition.title')}</h2>
        {overall.kind === 'estimate' && (
          <EstimateLabel methodVersion={overall.methodVersion} confidence={overall.confidence} />
        )}
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {suppressedShare === null ? (
          <Absent reason="not_measured" />
        ) : (
          <Withheld
            suppressedShare={suppressedShare}
            total={territory.total ?? null}
            source="electorate"
          />
        )}
        {cells === null || cells.length === 0 || overall.kind === 'refused' ? (
          <Absent reason="not_measured" />
        ) : (
          <>
            <div
              role="img"
              aria-label={tt('composition.chart')}
              className="rounded-pill flex h-2.5 gap-1 overflow-hidden"
            >
              {cells.map((cell, index) =>
                labellingOf(cell).kind === 'refused' ? null : (
                  <div
                    key={JSON.stringify(cell.attributes)}
                    data-slot="segment"
                    className={`h-full min-w-px ${toneOf(index)}`}
                    style={{ flexGrow: cell.count, flexBasis: 0 }}
                  />
                ),
              )}
            </div>
            <ul className="flex flex-wrap gap-x-3 gap-y-1">
              {ranked.slice(0, SHOWN_CELLS).map(({ cell, index }) => (
                <CellRow
                  key={JSON.stringify(cell.attributes)}
                  cell={cell}
                  tone={toneOf(index)}
                  overall={overall}
                />
              ))}
            </ul>
            {ranked.length > SHOWN_CELLS && (
              <details className="text-caption">
                <summary className="cursor-pointer text-primary-ink">
                  {tt('composition.showAll', { count: count(ranked.length, getLocale()) })}
                </summary>
                <ul className="mt-2 flex flex-wrap gap-x-3 gap-y-1">
                  {ranked.slice(SHOWN_CELLS).map(({ cell, index }) => (
                    <CellRow
                      key={JSON.stringify(cell.attributes)}
                      cell={cell}
                      tone={toneOf(index)}
                      overall={overall}
                    />
                  ))}
                </ul>
              </details>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
